// Wave 20.47 — inline title + description editor for an activity.
//
// - Read mode: shows the stored title + description (or a subtle
//   "Add a title" prompt if empty), with a pencil icon that flips
//   into edit mode.
// - Edit mode: title + description TextField, Save + Cancel buttons.
//   On open, if the stored title is empty we pre-populate both
//   fields from the server-side heuristic recommender so the rider
//   can accept or tweak instead of typing from scratch.
// - Save calls PATCH /sessions/:id/copy.
//
// Same component ships on the ActivityDetail page and (eventually)
// wherever session copy is editable.

import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { colors } from '../theme';
import { useUnitSystem } from '../services/useUnitSystem';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

function auth(): Record<string, string> {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

interface Props {
  sessionId: string;
  title: string | null;
  description: string | null;
  onSaved: (next: { title: string | null; description: string | null }) => void;
  /** When true, opens straight into edit mode with recommended
   *  values pre-filled. Used by the save flow — rider comes to
   *  session detail after saving and is prompted to accept/tweak
   *  the recommended copy. */
  autoOpenIfEmpty?: boolean;
}

export default function EditableSessionCopy({
  sessionId,
  title,
  description,
  onSaved,
  autoOpenIfEmpty,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(title ?? '');
  const [d, setD] = useState(description ?? '');
  const [saving, setSaving] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const units = useUnitSystem();

  const openEdit = async (fillIfEmpty: boolean) => {
    setT(title ?? '');
    setD(description ?? '');
    setEditing(true);
    if (fillIfEmpty && !title && !description) {
      // Pre-fill with the server heuristic.
      await recommend();
    }
  };

  const recommend = async () => {
    setRecommending(true);
    try {
      const res = await fetch(
        `${API}/sessions/${sessionId}/recommend-copy?units=${encodeURIComponent(units)}`,
        { headers: auth() },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.title) setT(data.title);
      if (data.description) setD(data.description);
    } catch {
      /* silent — user can type from scratch */
    } finally {
      setRecommending(false);
    }
  };

  useEffect(() => {
    if (!autoOpenIfEmpty || autoOpened) return;
    if (!title && !description) {
      setAutoOpened(true);
      void openEdit(true);
    }
  }, [autoOpenIfEmpty, autoOpened, title, description]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/sessions/${sessionId}/copy`, {
        method: 'PATCH',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: t, description: d }),
      });
      if (!res.ok) return;
      const data = await res.json();
      onSaved({ title: data.title ?? null, description: data.description ?? null });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: 'rgba(15,15,20,0.75)', border: `1px solid ${colors.border}` }}>
        <Stack spacing={1.5}>
          <TextField
            label="Title"
            value={t}
            onChange={(e) => setT(e.target.value.slice(0, 120))}
            fullWidth
            size="small"
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label="Description"
            value={d}
            onChange={(e) => setD(e.target.value.slice(0, 600))}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            inputProps={{ maxLength: 600 }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              onClick={recommend}
              disabled={recommending}
              startIcon={<AutoAwesomeIcon fontSize="small" />}
              sx={{ color: colors.brand, borderColor: colors.brand }}
              variant="outlined"
            >
              {recommending ? 'Thinking…' : 'Suggest'}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="small" variant="contained" onClick={save} disabled={saving} sx={{ backgroundColor: colors.brand, color: '#0a100c' }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  const showTitle = title && title.trim();
  const showDesc = description && description.trim();
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showTitle ? (
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.text }}>{title}</Typography>
          ) : (
            <Typography sx={{ color: colors.textMuted, fontStyle: 'italic' }}>Add a title</Typography>
          )}
          {showDesc && (
            <Typography sx={{ color: colors.textMuted, mt: 0.5, whiteSpace: 'pre-wrap' }}>{description}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={() => openEdit(!showTitle)} sx={{ color: colors.textMuted }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
